import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { authQueryOptions, useAuthQuery } from "@shared/api/auth";
import { type authClient, useAuthClient } from "../lib/authClient";

export type ResAuth = typeof authClient.$Infer.Session;

type AuthState =
	| { data: null; status: "PENDING" }
	| { data: null; status: "UNAUTHENTICATED" }
	| { data: ResAuth; status: "AUTHENTICATED" };

type AuthUtils = {
	ensureData: () => Promise<ResAuth | undefined>;
};

type AuthData = AuthState & AuthUtils;

export function useAuth(): AuthData {
	const authClient = useAuthClient();
	const authQuery = useAuthQuery(authClient);

	const queryClient = useQueryClient();

	useEffect(() => {
		if (authQuery.error === null) return;
		queryClient.setQueryData(["auth"], null);
	}, [authQuery.error, queryClient]);

	const utils: AuthUtils = {
		ensureData: () => {
			return queryClient.ensureQueryData(authQueryOptions(authClient));
		}
	};

	switch (true) {
		case authQuery.isLoading:
			return { ...utils, data: null, status: "PENDING" };

		case !authQuery.data?.data:
			return { ...utils, data: null, status: "UNAUTHENTICATED" };

		default:
			return { ...utils, data: authQuery.data.data!, status: "AUTHENTICATED" };
	}
}
